import React, { MouseEvent, PropsWithoutRef, ReactElement, useMemo, useRef } from 'react';
import { useCallback, useEffect, useState } from 'react';

import './Carousel.scss';
import Icon from '../Icon';
import { translate, getUniqueId } from '@micro-apps/env';

type CarouselProps = {
	id: string;
	elements: (ReactElement | undefined)[];
	elementsRef?: React.MutableRefObject<Record<string, HTMLDivElement> | null>;
	name?: string;
	showArrows?: boolean;
	showDots?: boolean;
	showOverlapArrows?: boolean;
	overlapArrowsClass?: string;
	onChange?: (active?: number, prev?: number, next?: number) => void;
	scrollIntoViewOptions?: ScrollIntoViewOptions;
	scrollRoot?: Element;
	startingIndex?: number;
};

export default function Carousel({
	id,
	elements = [],
	showArrows = false,
	showDots = false,
	showOverlapArrows = false,
	overlapArrowsClass = '',
	name = id,
	onChange,
	scrollIntoViewOptions = {
		behavior: 'smooth',
		block: 'nearest',
		inline: 'center',
	},
	scrollRoot,
	startingIndex = 0,
	elementsRef,
}: PropsWithoutRef<CarouselProps>): ReactElement {
	const imgId = useMemo(() => `${getUniqueId()}-carousel-${id}`, [id]);
	const [active, setActive] = useState(0);
	const [next, setNext] = useState(1);
	const [prev, setPrev] = useState(elements.length - 1);
	const [dots, setDots] = useState<ReactElement[]>([]);
	const [scrollingFromClick, setScrolling] = useState(false);
	const [statusClasses, setStatusClasses] = useState('');
	const carouselRef = useRef<HTMLDivElement>(null);
	const carouselItems: Record<string, HTMLDivElement> = useMemo(() => {
		return {};
	}, []);
	const setItemRef = (ref: HTMLDivElement) => {
		if (ref && ref.id) {
			carouselItems[ref.id] = ref;
		}
		if (elementsRef) {
			elementsRef.current = carouselItems;
		}
	};

	useEffect(() => {
		if (startingIndex > 0 && active !== startingIndex) {
			setActive(startingIndex);
			Object.values(carouselItems)[startingIndex]?.scrollIntoView({
				...scrollIntoViewOptions,
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore TS issue https://github.com/Microsoft/TypeScript/issues/28755
				behavior: 'instant',
			});
		}
		//run once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// works without JS due to using anchor, but js can make it nicer
	const onCarouselNav = useCallback(
		(e?: MouseEvent<HTMLAnchorElement>, scroll = true) => {
			let element: HTMLElement | undefined;
			if (e) {
				e.preventDefault();
				element = document.getElementById(e.currentTarget.hash.replace('#', '')) as HTMLDivElement;
			}

			let curr = active;
			try {
				if (element) {
					curr = element.dataset?.['index'] ? parseInt(element?.dataset?.['index']) : active;
				}
				// eslint-disable-next-line no-empty
			} catch {}
			if (curr !== active) {
				if (scroll) {
					setScrolling(true);
					element?.scrollIntoView(scrollIntoViewOptions);
				}
				setActive(curr);
			}
			const newNext = curr === elements.length - 1 ? 0 : curr + 1;
			const newPrev = curr === 0 ? elements.length - 1 : curr - 1;
			if (next !== newNext || prev !== newPrev) {
				setNext(newNext);
				setPrev(newPrev);
				if (onChange) {
					onChange(curr, newPrev, newNext);
				}
			}
		},
		[active, elements.length, next, onChange, prev, scrollIntoViewOptions]
	);
	useEffect(() => {
		let location = '';
		if (active === 0) {
			location = 'ReactCarousel-at-start';
		} else if (active === elements.length - 1) {
			location = 'ReactCarousel-at-end';
		} else {
			location = 'ReactCarousel-at-middle';
		}
		setStatusClasses(`active-${active} ${location}`);
	}, [active, elements.length]);

	useEffect(() => {
		if (showDots) {
			const dotsElements: ReactElement[] = [];
			if (elements.length > 1) {
				for (let i = 0; i < elements.length; i++) {
					const activeClass = active === i ? 'active' : '';
					dotsElements.push(
						// eslint-disable-next-line jsx-a11y/anchor-has-content
						<a
							key={i}
							onClick={onCarouselNav}
							href={`#${imgId}${i}`}
							className={`ReactCarousel-dot ${activeClass}`}
							aria-controls={`carousel-${id}`}
							aria-label={
								activeClass
									? translate('Slide ${0} is currently active', [i + 1 + '']) // eslint-disable-line no-template-curly-in-string
									: translate('Show Slide ${0}', [i + 1 + '']) // eslint-disable-line no-template-curly-in-string
							}
						></a>
					);
				}
			}
			setDots(dotsElements);
		}
	}, [active, elements.length, id, imgId, onCarouselNav, showDots]);

	useEffect(() => {
		if (active > -1) {
			onCarouselNav();
		}
	}, [active, onCarouselNav]);

	useEffect(() => {
		// keep active up to date when it is scrolling
		let carouselObserver: IntersectionObserver;
		if (carouselRef.current && Object.values(carouselItems).length) {
			carouselObserver = new IntersectionObserver(
				entries => {
					if (!scrollingFromClick) {
						const intersections: number[] = [];
						entries.forEach(entry => {
							if (entry.isIntersecting) {
								const element = entry.target as HTMLDivElement;
								try {
									const curr = element?.dataset?.['index']
										? parseInt(element?.dataset?.['index'])
										: -1;
									if (!isNaN(curr) && curr > -1) {
										intersections.push(curr);
									}
									// eslint-disable-next-line no-empty
								} catch {}
							}
						});
						if (!intersections.includes(active) && intersections.length) {
							setActive(intersections[0]);
						}
					}
				},
				{
					root: scrollRoot || carouselRef.current,
					rootMargin: '0px',
					threshold: 0.9,
				}
			);
			Object.values(carouselItems).forEach(item => {
				carouselObserver.observe(item);
			});
		}
		return () => {
			if (carouselObserver) {
				carouselObserver.disconnect();
			}
		};
	}, [carouselRef, carouselItems, active, scrollingFromClick, imgId, scrollRoot, elements]);
	let timeout: NodeJS.Timeout;
	const onScroll: React.UIEventHandler<HTMLDivElement> = () => {
		if (timeout !== undefined) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => {
			setScrolling(false);
		}, 50);
	};
	return (
		<section className={`ReactCarousel ${statusClasses}`}>
			<div className="ReactCarousel-carousel">
				{elements.length > 1 && showArrows && (
					<a
						aria-controls={`carousel-${id}`}
						onClick={onCarouselNav}
						href={`#${imgId}${prev}`}
						aria-label={translate('Previous slide')}
						className="ReactCarousel-nav-button ReactCarousel-nav-button-prev"
					>
						<Icon icon="chevron_left"></Icon>
					</a>
				)}
				<div className="ReactCarousel-wrapper">
					<div
						id={`carousel-${id}`}
						aria-roledescription="carousel"
						aria-label={name}
						className="ReactCarousel-items"
						ref={carouselRef}
						onScroll={onScroll}
					>
						{elements.map((element, i) => {
							return element ? (
								<div
									id={`${imgId}${i}`}
									data-index={i}
									className="ReactCarousel-item"
									role="group"
									aria-roledescription="slide"
									key={i}
									aria-label={`${i + 1} of ${elements.length}`}
									ref={setItemRef}
								>
									{element}
								</div>
							) : undefined;
						})}
					</div>
					{showOverlapArrows && elements.length > 1 ? (
						<div
							className={`h-[35px] flex flex-row items-center justify-between bg-surface text-text-primary text-caption ${overlapArrowsClass}`}
						>
							<a
								aria-controls={`carousel-${id}`}
								onClick={onCarouselNav}
								href={`#${imgId}${prev}`}
								aria-label={translate('Previous slide')}
								className="pl-100 pr-080 flex items-center justify-center text-text-primary hover:text-text-primary/80 transition-colors"
							>
								<span className="sr-only">{translate('Previous slide')}</span>
								<Icon icon="chevron_left"></Icon>
							</a>
							<span className="text-text-primary text-label whitespace-nowrap">
								{active + 1} / {elements.length}
							</span>
							<a
								aria-controls={`carousel-${id}`}
								onClick={onCarouselNav}
								href={`#${imgId}${next}`}
								aria-label={translate('Next slide')}
								className="pr-100 pl-080 flex items-center justify-center text-text-primary hover:text-text-primary/80 transition-colors"
							>
								<span className="sr-only">{translate('Next slide')}</span>
								<Icon icon="chevron_right"></Icon>
							</a>
						</div>
					) : (
						''
					)}
					{showDots && elements.length > 1 ? <div className="ReactCarousel-dots">{dots}</div> : ''}
				</div>
				{elements.length > 1 && showArrows && (
					<a
						aria-controls={`carousel-${id}`}
						onClick={onCarouselNav}
						href={`#${imgId}${next}`}
						aria-label={translate('Next slide')}
						className="ReactCarousel-nav-button ReactCarousel-nav-button-next"
					>
						<Icon icon="chevron_right"></Icon>
					</a>
				)}
			</div>
		</section>
	);
}
